import { AlertTriangle, Calendar, CheckCircle, Circle, ClipboardCheck, Clock, Flag, Search, XCircle, Zap } from "lucide-react";

// ========================================
// TIPOS E INTERFACES ATUALIZADOS
// ========================================

export type Status = "nao_iniciado" | "orcado" | "aprovar" | "aguardando" | "agendar" | "agendado" | "conferir" | "em_andamento" | "parado" | "concluido";
export type Prioridade = "baixa" | "media" | "alta" | "critica";

// ✅ Tipos de recorrência
export type PadraoRecorrencia = "daily" | "weekly" | "monthly" | "yearly" | "custom";
export type DiaSemana = "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday";

// ✅ Interface GrupoTarefa (mantida para compatibilidade)
export interface GrupoTarefa {
  id: string;
  nome: string;
  cor: string;
  ordem: number;
  obra_id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

// ✅ Interface Tarefa atualizada
export interface Tarefa {
  id: string;
  nome: string;
  descricao: string | null;
  status: Status;
  prioridade: Prioridade;
  data_inicio: string | null;
  /** Novo: indica se data_inicio tem horário real definido pelo usuário */
  data_inicio_tem_hora?: boolean | null;
  data_conclusao: string | null;
  data_limite: string | null;
  responsavel_id: string | null;
  responsaveis_ids?: string[];
  responsaveis?: Array<{
    id: string;
    nome: string;
    email: string;
  }>;
  
  // ✅ MANTIDO: grupo_id para compatibilidade (agora automático)
  grupo_id: string | null;
  grupos_tarefas?: GrupoTarefa;
  
  obra_id: string;
  ordem: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  
  // Campo de progresso
  progresso_pct: number; // 0-100
  
  // ✅ Campos de recorrência
  recorrente: boolean;
  padrao_recorrencia: PadraoRecorrencia | null;
  intervalo_recorrencia: number | null;
  dias_semana: DiaSemana[] | null;
  data_limite_recorrencia: string | null;
  tarefa_pai_id: string | null;
  proxima_ocorrencia: string | null;
  recorrencia_ativa: boolean;
  
  // Campos de relação existentes
  responsavel?: {
    id: string;
    nome: string;
    email: string;
  };
  created_by_tarefas_created_by_fkey?: {
    id: string;
    nome: string;
    email: string;
  };
  updated_by_tarefas_updated_by_fkey?: {
    id: string;
    nome: string;
    email: string;
  };
}

// ========================================
// CONSTANTES DE STATUS E PRIORIDADE
// ========================================

export const statusOptions = [
  {
    value: 'nao_iniciado' as Status,
    label: 'Não Iniciado',
    color: '#6B7280',
    bgColor: '#F3F4F6',
    icon: Circle
  },
  {
    value: 'orcado' as Status,
    label: 'Orçar',
    color: '#8B5CF6',
    bgColor: '#F3E8FF',
    icon: Circle
  },
  {
    value: 'aprovar' as Status,
    label: 'Aprovar',
    color: '#D97706',
    bgColor: '#FEF3C7',
    icon: ClipboardCheck
  },
  {
    value: 'aguardando' as Status,
    label: 'Aguardando',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    icon: Clock
  },
  {
    value: 'agendar' as Status,
    label: 'Agendar',
    color: '#EC4899',
    bgColor: '#FCE7F3',
    icon: Calendar
  },
  {
    value: 'agendado' as Status,
    label: 'Agendado',
    color: '#6366F1',
    bgColor: '#EEF2FF',
    icon: Calendar
  },
  {
    value: 'conferir' as Status,
    label: 'Conferir',
    color: '#0EA5E9',
    bgColor: '#E0F2FE',
    icon: Search
  },
  {
    value: 'em_andamento' as Status,
    label: 'Em Andamento',
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    icon: Zap
  },
  {
    value: 'parado' as Status,
    label: 'Parado',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    icon: XCircle
  },
  {
    value: 'concluido' as Status,
    label: 'Concluído',
    color: '#10B981',
    bgColor: '#D1FAE5',
    icon: CheckCircle
  }
];

export const prioridadeOptions = [
  { 
    value: 'baixa' as Prioridade, 
    label: '—', 
    color: '#6B7280', 
    icon: Circle 
  },
  { 
    value: 'media' as Prioridade, 
    label: 'Média', 
    color: '#F59E0B', 
    icon: Flag 
  },
  { 
    value: 'alta' as Prioridade, 
    label: 'Alta', 
    color: '#EF4444', 
    icon: AlertTriangle 
  },
  { 
    value: 'critica' as Prioridade, 
    label: 'Crítica', 
    color: '#DC2626', 
    icon: Zap 
  }
];

// ✅ Opções de recorrência
export const recorrenciaOptions = [
  {
    value: 'daily' as PadraoRecorrencia,
    label: 'Diário',
    description: 'Repetir todos os dias',
    icon: Calendar
  },
  {
    value: 'weekly' as PadraoRecorrencia,
    label: 'Semanal',
    description: 'Repetir semanalmente',
    icon: Calendar
  },
  {
    value: 'monthly' as PadraoRecorrencia,
    label: 'Mensal',
    description: 'Repetir mensalmente',
    icon: Calendar
  },
  {
    value: 'yearly' as PadraoRecorrencia,
    label: 'Anual',
    description: 'Repetir anualmente',
    icon: Calendar
  },
  {
    value: 'custom' as PadraoRecorrencia,
    label: 'Personalizado',
    description: 'Configurar intervalo específico',
    icon: Clock
  }
];

// ✅ Opções de dias da semana
export const diasSemanaOptions = [
  { value: 'sunday' as DiaSemana, label: 'Domingo', short: 'Dom' },
  { value: 'monday' as DiaSemana, label: 'Segunda', short: 'Seg' },
  { value: 'tuesday' as DiaSemana, label: 'Terça', short: 'Ter' },
  { value: 'wednesday' as DiaSemana, label: 'Quarta', short: 'Qua' },
  { value: 'thursday' as DiaSemana, label: 'Quinta', short: 'Qui' },
  { value: 'friday' as DiaSemana, label: 'Sexta', short: 'Sex' },
  { value: 'saturday' as DiaSemana, label: 'Sábado', short: 'Sáb' }
];

// ========================================
// ✅ FUNÇÕES PARA RECORRÊNCIA
// ========================================

/**
 * Verificar se uma tarefa é recorrente
 */
export function isTarefaRecorrente(tarefa: Tarefa): boolean {
  return tarefa.recorrente === true && tarefa.recorrencia_ativa === true;
}

/**
 * Verificar se uma tarefa é instância de recorrência (tem tarefa pai)
 */
export function isInstanciaRecorrente(tarefa: Tarefa): boolean {
  return tarefa.tarefa_pai_id !== null;
}

/**
 * Obter informações de recorrência formatadas
 */
export function getRecorrenciaInfo(tarefa: Tarefa): string {
  if (!tarefa.recorrente || !tarefa.padrao_recorrencia) return '';
  
  const opcao = recorrenciaOptions.find(r => r.value === tarefa.padrao_recorrencia);
  if (!opcao) return '';
  
  const intervalo = tarefa.intervalo_recorrencia || 1;
  
  switch (tarefa.padrao_recorrencia) {
    case 'daily':
      return intervalo === 1 ? 'Diário' : `A cada ${intervalo} dias`;
    case 'weekly':
      if (tarefa.dias_semana && tarefa.dias_semana.length > 0) {
        const dias = tarefa.dias_semana.map(dia => 
          diasSemanaOptions.find(d => d.value === dia)?.short || dia
        ).join(', ');
        return `Semanal (${dias})`;
      }
      return intervalo === 1 ? 'Semanal' : `A cada ${intervalo} semanas`;
    case 'monthly':
      return intervalo === 1 ? 'Mensal' : `A cada ${intervalo} meses`;
    case 'yearly':
      return intervalo === 1 ? 'Anual' : `A cada ${intervalo} anos`;
    default:
      return opcao.label;
  }
}

/**
 * Obter próxima ocorrência formatada
 */
export function getProximaOcorrenciaFormatada(tarefa: Tarefa): string {
  if (!tarefa.proxima_ocorrencia) return '';
  return formatDateTime(tarefa.proxima_ocorrencia);
}

// ========================================
// FUNÇÕES AUXILIARES PARA PROGRESSO
// ========================================

export function validarProgresso(progresso: number | null | undefined): number {
  if (progresso === null || progresso === undefined || isNaN(progresso)) {
    return 0;
  }
  return Math.max(0, Math.min(100, progresso));
}

export function validarProgressoParaSalvamento(progresso: number | null | undefined): number {
  if (progresso === null || progresso === undefined || isNaN(progresso)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(progresso)));
}

export function sincronizarProgressoComStatus(status: Status, progressoAtual?: number): number {
  if (status === 'concluido') {
    return 100;
  }
  
  if (progressoAtual !== undefined && progressoAtual !== null && progressoAtual >= 0 && progressoAtual <= 100) {
    return progressoAtual;
  }
  
  switch (status) {
    case 'em_andamento': return 50;
    case 'parado': return 25;
    case 'agendado': return 30;
    case 'agendar': return 20;
    case 'aguardando': return 15;
    case 'orcado': return 10;
    case 'conferir': return 40;
    case 'aprovar': return 90;
    case 'nao_iniciado':
    default: return 0;
  }
}

export function obterProgressoTarefa(tarefa: Tarefa): number {
  if (typeof tarefa.progresso_pct === 'number' && 
      !isNaN(tarefa.progresso_pct) && 
      tarefa.progresso_pct >= 0 && 
      tarefa.progresso_pct <= 100) {
    return parseInt(String(tarefa.progresso_pct));
  }
  
  return sincronizarProgressoComStatus(tarefa.status);
}

// ========================================
// FUNÇÕES DE HORÁRIO DE BRASÍLIA
// ========================================

export function getBrasiliaTimestamp(): string {
  return new Date().toISOString();
}

export function toBrasiliaTimestamp(dateTime: string | Date): string {
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  if (isNaN(date.getTime())) {
    throw new Error('Data inválida fornecida');
  }
  
  return date.toISOString();
}

// ========================================
// FUNÇÕES DE FORMATAÇÃO DE DATA/HORA
// ========================================

export function formatDateTime(dateTime: string | null, includeTime = true): string {
  if (!dateTime) return '-';
  
  try {
    const date = new Date(dateTime);
    
    if (isNaN(date.getTime())) return '-';
    
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    };
    
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return new Intl.DateTimeFormat('pt-BR', options).format(date);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '-';
  }
}

export function formatDateTimeDetalhado(dateTime: string | null): string {
  if (!dateTime) return '-';
  
  try {
    const comentarioDate = new Date(dateTime);
    if (isNaN(comentarioDate.getTime())) return '-';
    
    const agora = new Date();
    const diffMs = agora.getTime() - comentarioDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMs < 0) {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      }).format(comentarioDate);
    }
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    
    const brasiliaFormatter = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const hojeFormatter = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const hojeBrasilia = brasiliaFormatter.format(agora);
    const comentarioBrasilia = brasiliaFormatter.format(comentarioDate);
    
    if (hojeBrasilia === comentarioBrasilia) {
      const horaBrasilia = hojeFormatter.format(comentarioDate);
      return `hoje às ${horaBrasilia}`;
    }
    
    const ontem = new Date(agora);
    ontem.setDate(agora.getDate() - 1);
    const ontemBrasilia = brasiliaFormatter.format(ontem);
    
    if (comentarioBrasilia === ontemBrasilia) {
      const horaBrasilia = hojeFormatter.format(comentarioDate);
      return `ontem às ${horaBrasilia}`;
    }
    
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    }).format(comentarioDate);
    
  } catch (error) {
    console.error('Erro ao formatar data detalhada:', error);
    return dateTime;
  }
}

export function formatDate(dateTime: string | null): string {
  if (!dateTime) return '-';
  
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    
    // ✅ CORREÇÃO: Não forçar timezone específico para evitar shift de data
    // Usar timezone local do usuário para exibição
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
      // Removido timeZone: 'America/Sao_Paulo' para evitar problemas de conversão
    };
    
    return new Intl.DateTimeFormat('pt-BR', options).format(date);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '-';
  }
}

export function formatTime(dateTime: string | null): string {
  if (!dateTime) return '-';
  
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    }).format(date);
  } catch (error) {
    return '-';
  }
}

// ========================================
// ✅ FUNÇÕES PARA DATE vs TIMESTAMP (HORÁRIO OPCIONAL)
// ========================================

/**
 * ✅ NOVA: Detectar se uma string de data tem horário personalizado
 */
export function hasCustomTime(dateTimeString: string | null): boolean {
  if (!dateTimeString) return false;

  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return false;

    // 🔑 Sentinela: 12:00 local == "sem horário"
    const h = date.getHours();
    const m = date.getMinutes();
    const s = date.getSeconds();
    const ms = date.getMilliseconds();

    // Se for exatamente 12:00:00.000 (local), NÃO é horário customizado
    return !(h === 12 && m === 0 && s === 0 && ms === 0);
  } catch {
    return false;
  }
}

/**
 * ✅ Usa o flag quando disponível, senão cai no detector por horário (hasCustomTime).
 */
export function hasCustomTimeFlag(
  dateTimeString: string | null,
  flag?: boolean | null
): boolean {
  if (flag === true) return true;
  if (flag === false) return false;
  return hasCustomTime(dateTimeString);
}

/**
 * ✅ CORRIGIDA: Converter ISO string para datetime-local preservando o dia
 */
export function toDateTimeLocal(dateTime: string | null): string {
  if (!dateTime) return '';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 0-11
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    // YYYY-MM-DDTHH:mm (timezone local)
    return `${String(year).padStart(4,'0')}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}T${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`;
  } catch (error) {
    console.error('Erro em toDateTimeLocal:', error);
    return '';
  }
}

/**
 * ✅ CORRIGIDA: Converter ISO string para date local preservando o dia
 */
export function toDateLocal(dateString: string | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // ✅ CORREÇÃO: Não usar UTC aqui, pois queremos preservar o dia no timezone local
    // Usar o timezone local do usuário para exibição
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // getMonth() retorna 0-11
    const day = date.getDate();
    
    // ✅ FORMATO: YYYY-MM-DD preservando o dia correto
    return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Erro em toDateLocal:', error);
    return '';
  }
}

/**
 * ✅ ADAPTADA: Converter datetime-local para ISO (mantida compatibilidade)
 */
export function fromDateTimeLocal(dateTimeLocal: string): string | null {
  if (!dateTimeLocal) return null;
  
  try {
    const date = new Date(dateTimeLocal);
    if (isNaN(date.getTime())) return null;
    
    return date.toISOString();
  } catch (error) {
    return null;
  }
}

/**
 * ✅ CORRIGIDA: Converter date local para ISO preservando o dia selecionado
 */
export function fromDateLocal(dateLocal: string): string | null {
  if (!dateLocal) return null;
  
  try {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateLocal)) return null;
    
    // ✅ CORREÇÃO FUNDAMENTAL: Criar data no timezone local do usuário
    // Quando o usuário seleciona dia 14, queremos preservar exatamente o dia 14
    // Não usar UTC aqui, pois isso pode causar shift de data
    const [year, month, day] = dateLocal.split('-').map(Number);
    
    // ✅ NOVA ABORDAGEM: Criar data no timezone local, não UTC
    // Isso garante que o dia selecionado seja preservado exatamente
    const date = new Date(year, month - 1, day, 12, 0, 0, 0); // Meio-dia para evitar problemas de timezone
    
    if (isNaN(date.getTime())) return null;
    
    const result = date.toISOString();
    
    // ✅ DEBUG: Log para verificar conversão
    console.log('🔍 fromDateLocal DEBUG:', {
      input: dateLocal,
      year, month, day,
      dateLocal: date.toString(),
      dateISO: date.toISOString(),
      result
    });
    
    return result;
  } catch (error) {
    console.error('Erro em fromDateLocal:', error);
    return null;
  }
}

/**
 * ✅ NOVA: Converter datetime-local ou date local para ISO com lógica inteligente
 */
export function fromDateOrDateTimeLocal(input: string, includeTime: boolean): string | null {
  if (!input) return null;
  
  try {
    if (includeTime) {
      // Se incluir horário, usar datetime-local
      return fromDateTimeLocal(input);
    } else {
      // Se não incluir horário, usar date com horário padrão
      const dateOnly = input.includes('T') ? input.split('T')[0] : input;
      return fromDateLocal(dateOnly);
    }
  } catch (error) {
    console.error('Erro ao converter data:', error);
    return null;
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// ========================================
// VALIDAÇÕES E VERIFICAÇÕES DE DATA/HORA
// ========================================

export function isTaskOverdue(dataInicio: string | null, status: Status): boolean {
  if (!dataInicio || status === 'concluido') return false;
  
  try {
    const inicio = new Date(dataInicio);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0); // Normalizar para início do dia
    
    return inicio < hoje;
  } catch (error) {
    return false;
  }
}

export function isTaskDueToday(dataInicio: string | null): boolean {
  if (!dataInicio) return false;
  
  try {
    const inicio = new Date(dataInicio);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0); // Normalizar para início do dia
    
    return inicio.toDateString() === hoje.toDateString();
  } catch (error) {
    return false;
  }
}

export function isTaskDueSoon(dataInicio: string | null, horasAntecedencia = 2): boolean {
  if (!dataInicio) return false;
  
  try {
    const inicio = new Date(dataInicio);
    const agora = new Date();
    const limite_antecedencia = new Date(agora.getTime() + (horasAntecedencia * 60 * 60 * 1000));
    
    return inicio <= limite_antecedencia && inicio > agora;
  } catch (error) {
    return false;
  }
}

export function getTimeUntilDeadline(dataInicio: string | null): string {
  if (!dataInicio) return '';
  
  try {
    const inicio = new Date(dataInicio);
    const agora = new Date();
    const diff = inicio.getTime() - agora.getTime();
    
    if (diff < 0) {
      const diffAbs = Math.abs(diff);
      const dias = Math.floor(diffAbs / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diffAbs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (dias > 0) return `${dias}d atrasado`;
      if (horas > 0) return `${horas}h atrasado`;
      return 'Atrasado';
    }
    
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
    const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (dias > 0) return `${dias}d restantes`;
    if (horas > 0) return `${horas}h restantes`;
    if (minutos > 0) return `${minutos}min restantes`;
    return 'Começa agora';
  } catch (error) {
    return '';
  }
}

export function isValidDateTime(dateTime: string): boolean {
  if (!dateTime) return false;
  
  try {
    const date = new Date(dateTime);
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
}

export function getCurrentDateTimeLocal(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localDate = new Date(now.getTime() - (offset * 60 * 1000));
  return localDate.toISOString().slice(0, 16);
}

/**
 * ✅ NOVA: Obter data atual apenas (sem horário)
 */
export function getCurrentDateLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ————————————————
// UUID helpers
// ————————————————
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function isUUID(v: unknown): v is string {
  return typeof v === 'string' && UUID_RE.test(v);
}

/**
 * ✅ NOVA: Obter data atual no formato ISO sem problemas de timezone
 */
export function getCurrentDateISO(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  
  // ✅ Criar data no timezone local para evitar shift
  const localDate = new Date(year, month, day, 0, 0, 0, 0);
  return localDate.toISOString();
}

export function validateDateRange(dataInicio: string | null, dataLimite: string | null): {
  isValid: boolean;
  message?: string;
} {
  if (!dataInicio || !dataLimite) {
    return { isValid: true };
  }
  
  try {
    const inicio = new Date(dataInicio);
    const limite = new Date(dataLimite);
    
    if (inicio > limite) {
      return {
        isValid: false,
        message: 'A data de início deve ser anterior à data limite'
      };
    }
    
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      message: 'Datas inválidas'
    };
  }
}

// ========================================
// FUNÇÕES LEGADAS REMOVIDAS
// ========================================
// ✅ REMOVIDO: isTarefaAtrasada e isTarefaHoje que usavam data_limite
// Agora usamos data_inicio para validações de prazos

// ========================================
// FUNÇÕES DE UTILIDADE PARA STATUS/PRIORIDADE
// ========================================

export const getStatusColor = (status: Status) => {
  const statusObj = statusOptions.find(s => s.value === status);
  return statusObj ? { color: statusObj.color, bgColor: statusObj.bgColor } : { color: '#6B7280', bgColor: '#F3F4F6' };
};

export const getPrioridadeColor = (prioridade: Prioridade) => {
  const prioridadeObj = prioridadeOptions.find(p => p.value === prioridade);
  return prioridadeObj ? { color: prioridadeObj.color, icon: prioridadeObj.icon } : { color: '#6B7280', icon: Circle };
};

// ========================================
// VALIDAÇÃO E LIMPEZA DE DADOS ATUALIZADA
// ========================================

export const validarTarefa = (dados: any): { valido: boolean; erros: string[] } => {
  const erros: string[] = [];
  
  if (!dados.nome || !dados.nome.trim()) {
    erros.push('Nome da tarefa é obrigatório');
  }
  
  if (dados.nome && dados.nome.length > 255) {
    erros.push('Nome da tarefa deve ter no máximo 255 caracteres');
  }
  
  if (dados.descricao && dados.descricao.length > 1000) {
    erros.push('Descrição deve ter no máximo 1000 caracteres');
  }
  
  if (dados.status && !statusOptions.find(s => s.value === dados.status)) {
    erros.push('Status inválido');
  }
  
  if (dados.prioridade && !prioridadeOptions.find(p => p.value === dados.prioridade)) {
    erros.push('Prioridade inválida');
  }
  
  // ✅ Validação de recorrência
  if (dados.recorrente) {
    if (dados.padrao_recorrencia && !recorrenciaOptions.find(r => r.value === dados.padrao_recorrencia)) {
      erros.push('Padrão de recorrência inválido');
    }
    
    if (dados.intervalo_recorrencia && (dados.intervalo_recorrencia < 1 || dados.intervalo_recorrencia > 365)) {
      erros.push('Intervalo de recorrência deve estar entre 1 e 365');
    }
    
    if (dados.dias_semana && Array.isArray(dados.dias_semana)) {
      const diasValidos = diasSemanaOptions.map(d => d.value);
      const diasInvalidos = dados.dias_semana.filter((dia: string) => !diasValidos.includes(dia as DiaSemana));
      if (diasInvalidos.length > 0) {
        erros.push('Dias da semana inválidos: ' + diasInvalidos.join(', '));
      }
    }
  }
  
  if (dados.progresso_pct !== undefined && dados.progresso_pct !== null) {
    const progresso = parseInt(String(dados.progresso_pct));
    if (isNaN(progresso) || progresso < 0 || progresso > 100) {
      erros.push('Progresso deve ser um número inteiro entre 0 e 100');
    }
  }
  
  if (dados.data_inicio && dados.data_conclusao) {
    const validacaoInicioConlusao = validateDateRange(dados.data_inicio, dados.data_conclusao);
    if (!validacaoInicioConlusao.isValid) {
      erros.push('Data de início não pode ser posterior à data de conclusão');
    }
  }
  
  return {
    valido: erros.length === 0,
    erros
  };
};

export const limparDadosTarefa = (dados: any) => {
  const dadosLimpos = { ...dados };
  
  // Campos que devem ser null quando vazios
  const camposNulaveis = [
    'descricao', 'data_inicio', 'data_conclusao',
    'responsavel_id',
    // ✅ Campos de recorrência
    'padrao_recorrencia', 'data_limite_recorrencia', 'tarefa_pai_id', 'proxima_ocorrencia'
  ];
  
  camposNulaveis.forEach(campo => {
    if (dadosLimpos[campo] === '' || dadosLimpos[campo] === undefined) {
      dadosLimpos[campo] = null;
    }
  });
  
  // ✅ Tratamento específico para campos de recorrência
  if (!dadosLimpos.recorrente) {
    dadosLimpos.recorrente = false;
    dadosLimpos.padrao_recorrencia = null;
    dadosLimpos.intervalo_recorrencia = null;
    dadosLimpos.dias_semana = null;
    dadosLimpos.data_limite_recorrencia = null;
    dadosLimpos.recorrencia_ativa = false;
  } else {
    dadosLimpos.recorrencia_ativa = true;
    if (!dadosLimpos.intervalo_recorrencia) {
      dadosLimpos.intervalo_recorrencia = 1;
    }
  }
  
  // Tratar progresso
  if (dadosLimpos.progresso_pct === '' || dadosLimpos.progresso_pct === undefined) {
    dadosLimpos.progresso_pct = 0;
  } else if (typeof dadosLimpos.progresso_pct === 'string') {
    dadosLimpos.progresso_pct = parseInt(dadosLimpos.progresso_pct) || 0;
  }
  
  dadosLimpos.progresso_pct = validarProgressoParaSalvamento(dadosLimpos.progresso_pct);
  
  return dadosLimpos;
};

// ========================================
// FUNÇÕES AUXILIARES PARA UX
// ========================================

/**
 * ✅ ADAPTADA: Gerar data/hora rápida com suporte a toggle de horário
 */
export const gerarDataLimiteRapida = (opcao: 'hoje' | 'amanha' | 'semana' | 'mes', incluirHorario = true): string => {
  const agora = new Date();
  let novaData: Date;

  switch (opcao) {
    case 'hoje':
      novaData = new Date(agora);
      if (incluirHorario) {
        novaData.setHours(18, 0, 0, 0);
      } else {
        novaData.setHours(23, 59, 59, 999);
      }
      break;
    case 'amanha':
      novaData = new Date(agora);
      novaData.setDate(agora.getDate() + 1);
      if (incluirHorario) {
        novaData.setHours(9, 0, 0, 0);
      } else {
        novaData.setHours(23, 59, 59, 999);
      }
      break;
    case 'semana':
      novaData = new Date(agora);
      novaData.setDate(agora.getDate() + 7);
      if (incluirHorario) {
        novaData.setHours(17, 0, 0, 0);
      } else {
        novaData.setHours(23, 59, 59, 999);
      }
      break;
    case 'mes':
      novaData = new Date(agora);
      novaData.setMonth(agora.getMonth() + 1);
      if (incluirHorario) {
        novaData.setHours(17, 0, 0, 0);
      } else {
        novaData.setHours(23, 59, 59, 999);
      }
      break;
    default:
      novaData = new Date(agora);
      if (incluirHorario) {
        novaData.setHours(18, 0, 0, 0);
      } else {
        novaData.setHours(23, 59, 59, 999);
      }
  }

  return incluirHorario ? toDateTimeLocal(novaData.toISOString()) : toDateLocal(novaData.toISOString());
};

// ✅ REMOVIDO: sortTarefasByDataLimite e getDataLimiteInfo
// Essas funções usavam data_limite que não está mais em uso na UI

// ========================================
// FUNÇÕES DE TIMER PARA EVITAR LOGS DUPLICADOS
// ========================================

const timers = new Set<string>();

export function time(label: string) {
  if (timers.has(label)) return;
  timers.add(label);
  console.time(label);
}

export function timeEnd(label: string) {
  if (!timers.has(label)) return;
  timers.delete(label);
  console.timeEnd(label);
}